import type { Logger } from "@expert/logging";

import { createContext, useContext, useMemo } from "react";

import type { ShadowComparisonResult } from "./shadow/types";
import type { GaiaWebsocketContext, OutboundMessageTransform } from "./types";

import { useGaiaWebsocketInternal } from "./hooks/useGaiaWebsocket";

export const GaiaContext = createContext<GaiaWebsocketContext | null>(null);
interface GaiaWebsocketProviderProps {
    url: string;
    application: "expert-workspace" | "expert-assist";
    token: string;
    identity: string;
    logger: Logger;
    children?: React.ReactNode;
    /** Base URL of the Simplr shadow websocket (dark launch). */
    shadowUrl?: string;
    /** When true (and `shadowUrl` set), opens the Simplr shadow connection for comparison. */
    shadowEnabled?: boolean;
    /** Invoked for each GAIA/Simplr message difference (never for equal messages). */
    onShadowComparison?: (result: ShadowComparisonResult) => void;
    /** Applied to each consumer-initiated message before it is sent on the live socket. */
    transformOutbound?: OutboundMessageTransform;
    /** Applied to each mirrored message before it is sent on the Simplr shadow socket. */
    shadowTransformOutbound?: OutboundMessageTransform;
    /** True when the resolved live connection is Simplr (not legacy GAIA). Surfaced on the context. */
    isSimplrLive?: boolean;
}

export function GaiaWebsocketProvider({
    url,
    application,
    token,
    identity,
    children,
    logger,
    shadowUrl,
    shadowEnabled,
    onShadowComparison,
    transformOutbound,
    shadowTransformOutbound,
    isSimplrLive = false,
}: GaiaWebsocketProviderProps) {
    const shadow = useMemo(
        () => ({
            url: shadowUrl,
            enabled: shadowEnabled,
            onComparison: onShadowComparison,
            transformOutbound: shadowTransformOutbound,
        }),
        [shadowUrl, shadowEnabled, onShadowComparison, shadowTransformOutbound],
    );
    const webSocketObject = useGaiaWebsocketInternal({
        baseUrl: url,
        application,
        token,
        identity,
        logger,
        shadow,
        transformOutbound,
    });
    // Surface which transport actually resolved (not just the flag) so consumers can gate
    // Simplr-only behaviour on the live connection.
    const contextValue = useMemo<GaiaWebsocketContext>(
        () => ({ ...webSocketObject, isSimplrLive }),
        [webSocketObject, isSimplrLive],
    );
    return <GaiaContext.Provider value={contextValue}>{children}</GaiaContext.Provider>;
}

export function useGaiaWebsocket() {
    const context = useContext(GaiaContext);
    return context;
}
